import { useEffect, useState } from 'react';

import { Box, Image, Text } from '@opendoor/bricks-next';

const SIZES = {
  sm: { size: '$32x', font: '$bodySmall' },
  md: { size: '$60x', font: '$bodyMedium' },
  lg: { size: '$72x', font: '$bodyLarge' },
} as const;

type AvatarSize = keyof typeof SIZES;

export type AvatarProps = {
  initials: string;
  src?: string;
  alt?: string;
  size?: AvatarSize;
};

const isImageLoaded = (img: HTMLImageElement): boolean => img.complete && img.naturalWidth > 0;

const useImageValid = (src?: string): boolean => {
  const [isValid, setIsValid] = useState(() => {
    if (!src || typeof window === 'undefined') {
      return false;
    }
    const img = new window.Image();
    img.src = src;
    return isImageLoaded(img);
  });

  useEffect(() => {
    // Reset validity when src is removed to prevent stale state if a new src is set later
    if (!src) {
      setIsValid(false);
      return;
    }
    let cancelled = false;
    const img = new window.Image();
    img.src = src;
    if (isImageLoaded(img)) {
      setIsValid(true);
      return;
    }
    img.onload = () => {
      if (!cancelled && isImageLoaded(img)) {
        setIsValid(true);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setIsValid(false);
      }
    };
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return isValid;
};

export const Avatar: React.FC<AvatarProps> = ({ initials, src, alt, size = 'md' }) => {
  const isImageValid = useImageValid(src);
  const { size: sizeToken, font } = SIZES[size];

  return (
    <Box
      display="flex"
      width={sizeToken}
      height={sizeToken}
      borderRadius="$full"
      backgroundColor="$backgroundTertiary"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
    >
      {isImageValid && src ? (
        <Image
          source={{ uri: src }}
          alt={alt ?? ''}
          width={sizeToken}
          height={sizeToken}
          variant="circle"
        />
      ) : (
        <Text typography={font} fontWeight="$medium" tag="span">
          {initials}
        </Text>
      )}
    </Box>
  );
};
